import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { siteMediaAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ChevronRight, Star, Users, Calendar, CreditCard, Sparkles, Quote, MapPin, Mail, Phone, Play, ArrowRight, Download, FileText, Instagram, Facebook } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Toaster, toast } from 'sonner';
import Navbar from '../components/Navbar';
import { HsmBanner } from '../components/HsmBanner';
import { FreeTrialBanner, FreeTrialFloating } from '../components/FreeTrialCTA';
import SEO from '../components/common/SEO';
import { sharedFaqs } from './SeoLandingPage';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

// Default gallery images (fallback)
const defaultGalleryImages = [
  'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4is8revz_040.JPG',
  'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/s23hufcw_206.JPG',
  'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/aanvvstv_228.JPG',
  'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/r9lohg1n_06dff212-c293-43e0-822e-6a84fcaad4b5.JPG',
  'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4z7weapm_9bdc1476-7b0c-4ea7-8f1a-f5a6f9390a40.JPG',
  'https://customer-assets.emergentagent.com/job_upa-scheduler/artifacts/1ca0g431_331.JPG',
  'https://customer-assets.emergentagent.com/job_upa-scheduler/artifacts/1el61v6e_367.JPG',
  'https://customer-assets.emergentagent.com/job_upa-scheduler/artifacts/d48l91yg_374.JPG',
  'https://customer-assets.emergentagent.com/job_upa-scheduler/artifacts/ssy2libo_382.JPG',
  'https://customer-assets.emergentagent.com/job_upa-scheduler/artifacts/9ed1rkop_385.JPG',
];

// UPA splash badge logo (Jun 2026)
const UPA_LOGO = 'https://customer-assets.emergentagent.com/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/bn56j1c1_gradient%20print.png';

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [typedText, setTypedText] = useState(['', '', '']);
  const [currentLine, setCurrentLine] = useState(0);
  const [headlineVisible, setHeadlineVisible] = useState(true);
  const [siteMedia, setSiteMedia] = useState({});
  const [galleryImages, setGalleryImages] = useState(defaultGalleryImages);

  const headlines = ['BELIEVE', 'LEARN', 'ACHIEVE'];

  // Fetch site media settings and gallery
  useEffect(() => {
    const fetchSiteMedia = async () => {
      try {
        // Fetch page settings
        const res = await siteMediaAPI.getPageSettings('landing');
        const mediaMap = {};
        res.data.forEach(item => {
          mediaMap[item.section] = item;
        });
        setSiteMedia(mediaMap);
        
        // Fetch gallery images
        const galleryRes = await siteMediaAPI.getGallery();
        if (galleryRes.data?.images && galleryRes.data.images.length > 0) {
          setGalleryImages(galleryRes.data.images.map(img => img.url));
        }
      } catch (error) {
        console.log('Using default media');
      }
    };
    fetchSiteMedia();
  }, []);

  // Typewriter effect
  useEffect(() => {
    const typeText = async () => {
      for (let lineIndex = 0; lineIndex < headlines.length; lineIndex++) {
        setCurrentLine(lineIndex);
        const text = headlines[lineIndex];
        for (let charIndex = 0; charIndex <= text.length; charIndex++) {
          await new Promise(resolve => setTimeout(resolve, 80));
          setTypedText(prev => {
            const newText = [...prev];
            newText[lineIndex] = text.slice(0, charIndex);
            return newText;
          });
        }
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    };
    
    typeText();
  }, []);

  // Auto-play video on mount and fade out headline after 5 seconds
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay was prevented, video will show poster instead
      });
    }
    
    // Fade out headline after 5 seconds of video playing
    const fadeTimer = setTimeout(() => {
      setHeadlineVisible(false);
    }, 5000);
    
    return () => clearTimeout(fadeTimer);
  }, []);

  const testimonials = [
    {
      quote: "This school has it all, learn to perform from industry professionals",
      author: "Suzanne Shaw",
      image: "https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/3io8fwpk_Suzanne_Shaw.webp"
    },
    {
      quote: "If you want to meet like minded people and become a successful actor then UPA is for you",
      author: "Jason Manford",
      image: "https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/kxsqh3xy_Jason-Manford-_Olivier_Awards_2019_Pamela_Raith_Photography1_Standard.jpg"
    },
    {
      quote: "If you want to make it on Broadway or in the West End you have to train and UPA is where you want to go!",
      author: "Cory English",
      role: "Broadway and West End Star",
      image: "https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/2d46keau_news-body-image_38172.jpg"
    },
    {
      quote: "Pop down to Universal Performing Arts, you can sing, you can dance, you can even end up like me - not!",
      author: "Louis Spence",
      image: "https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/ldazkj49_JS59491165.png.avif"
    },
  ];

  const classCategories = [
    { name: 'DANCE', description: 'Ballet, Contemporary, Jazz & Street', image: 'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/67s01etr_A7402917-Enhanced-NR-2-116.JPG' },
    { name: 'DRAMA', description: 'Acting, Improvisation & Performance', image: 'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/7xfauwlb_Fame-092.JPG' },
    { name: 'SINGING', description: 'Vocal Training & Musical Theatre', image: 'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/33mkqy5l_j-Bethaney-Never-Enough-006.JPG' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEO page="home" />
      <Navbar />
      <HsmBanner />
      <Toaster position="top-right" richColors />
      
      {/* Hero Section - Full Screen Video */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Full Screen Video Background */}
        <div className="absolute inset-0">
          {siteMedia.hero_video?.media_type === 'embed' && siteMedia.hero_video?.embed_url ? (
            <iframe
              src={siteMedia.hero_video.embed_url}
              className="absolute w-full h-full object-cover"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ pointerEvents: 'none' }}
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster={siteMedia.hero_image?.url || "https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4is8revz_040.JPG"}
              className="absolute w-full h-full object-cover"
            >
              <source src={siteMedia.hero_video?.url || "https://customer-assets.emergentagent.com/job_b293e9ea-1983-4cbc-af83-015ce5ba04ed/artifacts/0q1y7zg4_UPA%20ad%20website%281%29.mp4"} type="video/mp4" />
            </video>
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/90" />
        </div>

        {/* Hero Content - Centered */}
        <div className="relative z-10 w-full text-center px-6">
          <div className="max-w-5xl mx-auto space-y-8 relative">
            {/* Larger badge text */}
            <div className="inline-block space-y-2">
              <span className="block font-accent text-base sm:text-lg md:text-xl tracking-[0.3em] text-[#FF007F] uppercase">
                Milton Keynes Premier Performing Arts School
              </span>
              <span className="block font-accent text-sm sm:text-base tracking-[0.2em] text-[#FFD700] uppercase">
                Voted Best Theatre School in Milton Keynes
              </span>
            </div>
            
            {/* Main Headline with Typewriter Effect and Fade Out */}
            <h1 className={`font-heading text-5xl sm:text-7xl lg:text-9xl font-bold uppercase leading-none tracking-tight transition-opacity duration-1000 ${headlineVisible ? 'opacity-100' : 'opacity-0'}`}>
              <span className="block min-h-[1.1em]">
                {typedText[0]}
                {currentLine === 0 && <span className="animate-pulse">|</span>}
              </span>
              <span className="block min-h-[1.1em]">
                {typedText[1]}
                {currentLine === 1 && <span className="animate-pulse">|</span>}
              </span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] to-[#7000FF] min-h-[1.1em]">
                {typedText[2]}
                {currentLine === 2 && typedText[2].length < 7 && <span className="text-white animate-pulse">|</span>}
              </span>
            </h1>
            
            {/* UPA Logo - Fades in after headline fades out */}
            <div className={`transition-opacity duration-1000 ${!headlineVisible ? 'opacity-100' : 'opacity-0'} absolute left-1/2 -translate-x-1/2 top-[50%] -translate-y-[55%] pointer-events-none`}>
              <img 
                src={UPA_LOGO} 
                alt="Universal Performing Arts"
                className="w-[70vw] max-w-[700px] h-auto opacity-20"
              />
            </div>
            
            {/* Tagline */}
            <p className="font-accent text-xl sm:text-2xl tracking-wide text-white/80 uppercase">
              Together We Are Universal
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <Button 
                size="lg" 
                className="bg-[#FF007F] hover:bg-[#D6006A] text-white font-bold uppercase tracking-wider px-10 py-6 rounded-none shadow-neon-pink"
                onClick={() => navigate('/free-trial')}
                data-testid="hero-enroll-btn"
              >
                BOOK FREE TRIAL
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-10 py-6 rounded-none"
                onClick={() => navigate('/classes')}
                data-testid="hero-classes-btn"
              >
                VIEW CLASSES
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* SEO Intro — Theatre School in Milton Keynes */}
      <section className="py-16 px-6 lg:px-12 bg-[#0a0a0a] border-t border-[#1a1a1a]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-accent text-base sm:text-lg tracking-[0.3em] text-[#7000FF] uppercase">
            Theatre School in Milton Keynes
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-4 mb-6">
            Acting · Singing · Dance — all <span className="text-[#FF007F]">under one roof</span>
          </h2>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed">
            Universal Performing Arts is the Milton Keynes theatre school trusted by hundreds of local families.
            Weekly musical theatre, acting, singing and dance classes for ages 4–18, taught by West End and TV
            professionals — with termly showcases and an annual full-scale production. Saturdays at
            Kents Hill (MK7&nbsp;6BZ), weekdays at Watling Academy (MK8&nbsp;1EP).
          </p>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-accent text-base sm:text-lg tracking-[0.3em] text-[#7000FF] uppercase">Our Training</span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4">
              TRIPLE THREAT <span className="text-[#FF007F]">PROGRAMMES</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {classCategories.map((category, idx) => (
              <div 
                key={idx}
                className="group relative h-[500px] overflow-hidden cursor-pointer"
                onClick={() => navigate('/classes')}
              >
                {/* Image */}
                <img 
                  src={category.image} 
                  alt={category.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                {/* Purple tint on hover */}
                <div className="absolute inset-0 bg-[#7000FF]/0 group-hover:bg-[#7000FF]/20 transition-colors duration-500" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="font-heading text-3xl lg:text-4xl font-bold uppercase mb-2 group-hover:text-[#FF007F] transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-white/70 mb-4">{category.description}</p>
                  <div className="flex items-center gap-2 text-[#FF007F] font-semibold uppercase text-sm tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Explore</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-r from-[#7000FF] to-[#FF007F]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-heading text-5xl lg:text-6xl font-bold">25+</div>
              <div className="text-white/80 uppercase tracking-wider text-sm mt-2">Years Experience</div>
            </div>
            <div>
              <div className="font-heading text-5xl lg:text-6xl font-bold">1000+</div>
              <div className="text-white/80 uppercase tracking-wider text-sm mt-2">Students Trained</div>
            </div>
            <div>
              <div className="font-heading text-5xl lg:text-6xl font-bold">100%</div>
              <div className="text-white/80 uppercase tracking-wider text-sm mt-2">Passion</div>
            </div>
            <div>
              <div className="font-heading text-5xl lg:text-6xl font-bold">1</div>
              <div className="text-white/80 uppercase tracking-wider text-sm mt-2">Dream Team</div>
            </div>
          </div>
        </div>
      </section>

      {/* Celebrity Endorsements */}
      <section className="py-24 px-6 lg:px-12 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-accent text-base sm:text-lg tracking-[0.3em] text-[#7000FF] uppercase">Trusted By The Best</span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4">
              CELEBRITY <span className="text-[#FF007F]">ENDORSEMENTS</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div 
                key={idx}
                className="bg-[#111] border border-[#222] p-6 hover:border-[#FF007F]/50 transition-all duration-300 group"
              >
                <div className="relative w-20 h-20 mx-auto mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.author}
                    loading="lazy"
                    className="w-full h-full object-cover rounded-full grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#FF007F] rounded-full flex items-center justify-center">
                    <Quote className="w-4 h-4 text-white" />
                  </div>
                </div>
                <blockquote className="text-white/70 text-sm italic text-center mb-4">
                  "{testimonial.quote}"
                </blockquote>
                <p className="font-heading text-lg font-bold text-center uppercase">{testimonial.author}</p>
                {testimonial.role && (
                  <p className="text-[#FF007F] text-xs text-center uppercase tracking-wider">{testimonial.role}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section - Faster Scroll */}
      <section className="py-24 px-6 lg:px-12 bg-[#0a0a0a] overflow-hidden">
        <div className="max-w-7xl mx-auto mb-12">
          <div className="text-center">
            <span className="font-accent text-base sm:text-lg tracking-[0.3em] text-[#7000FF] uppercase">Our Students In Action</span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4">
              GALLERY
            </h2>
          </div>
        </div>
        
        {/* Infinite Scroll Gallery - Faster */}
        <div className="relative">
          <div className="flex gap-4 animate-scroll-fast">
            {[...galleryImages, ...galleryImages, ...galleryImages].map((image, idx) => (
              <div 
                key={idx}
                className="flex-shrink-0 w-80 h-56 overflow-hidden rounded-lg"
              >
                <img 
                  src={image} 
                  alt={`Gallery ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
        
        <style>{`
          @keyframes scroll-fast {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.33%); }
          }
          .animate-scroll-fast {
            animation: scroll-fast 15s linear infinite;
          }
          .animate-scroll-fast:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* Why Choose UPA */}
      <section className="py-24 px-6 lg:px-12 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-accent text-base sm:text-lg tracking-[0.3em] text-[#7000FF] uppercase">Why UPA?</span>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4 mb-8">
                THE UPA <span className="text-[#FF007F]">DIFFERENCE</span>
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-[#FF007F]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl uppercase font-bold mb-2">Industry-Standard Training</h3>
                    <p className="text-white/60">Learn from professionals who have performed on West End stages and in major productions.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#7000FF]/10 border border-[#7000FF]/30 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[#7000FF]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl uppercase font-bold mb-2">Family Discounts</h3>
                    <p className="text-white/60">30% off for additional siblings - because performing arts brings families together.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-[#FF007F]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl uppercase font-bold mb-2">Flexible Booking</h3>
                    <p className="text-white/60">Easy online enrollment with secure payment options and flexible term lengths.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/s23hufcw_206.JPG"
                  alt="UPA Performance"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative border */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#FF007F] -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#7000FF] to-[#FF007F]" />
        <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4is8revz_040.JPG')] bg-cover bg-center opacity-20" />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mb-6">
            READY TO TAKE THE STAGE?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join hundreds of students who have discovered their passion at Universal Performing Arts.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-white text-[#FF007F] hover:bg-white/90 font-bold uppercase tracking-wider px-10 py-6 rounded-none"
              onClick={() => navigate('/free-trial')}
              data-testid="cta-enroll-btn"
            >
              BOOK FREE TRIAL
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-bold uppercase tracking-wider px-10 py-6 rounded-none"
              onClick={() => navigate('/contact')}
              data-testid="cta-contact-btn"
            >
              CONTACT US
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 lg:px-12 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-accent text-base sm:text-lg tracking-[0.3em] text-[#7000FF] uppercase">
              Common Questions
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4">
              FREQUENTLY <span className="text-[#FF007F]">ASKED</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
            {sharedFaqs.map((f, i) => (
              <AccordionItem key={i} value={`home-item-${i}`} className="border-[#222]">
                <AccordionTrigger
                  className="text-left text-white hover:text-[#FF007F] font-heading uppercase text-base"
                  data-testid={`home-faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Prospectus Download - Fixed Link */}
      <section className="py-16 px-6 lg:px-12 bg-[#0a0a0a] border-t border-[#222]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-heading text-2xl uppercase font-bold mb-4">DOWNLOAD OUR PROSPECTUS</h3>
          <p className="text-white/60 mb-6">Get all the information about our classes, schedules, and pricing.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/prospectus"
              className="inline-flex items-center justify-center gap-2 bg-[#FF007F] hover:bg-[#D6006A] text-white px-8 py-4 font-bold uppercase tracking-wider transition-colors"
              data-testid="view-prospectus-btn"
            >
              <FileText className="w-5 h-5" />
              View Online
            </a>
            <a 
              href="https://customer-assets.emergentagent.com/job_bfb4c33f-5801-45fc-b2e5-0237443f0ff9/artifacts/e2sfn8pj_UPA%20Prospectus%20web.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F] hover:text-white px-8 py-4 font-bold uppercase tracking-wider transition-colors"
              data-testid="download-prospectus-btn"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#050505] border-t border-[#222]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src={UPA_LOGO} 
                alt="UPA Logo" 
                className="h-20 w-auto mb-4"
              />
              <p className="text-white/50 text-sm">
                Training the stars of tomorrow in Acting, Singing, and Dance since 2014.
              </p>
            </div>
            <div>
              <h4 className="font-heading text-lg uppercase font-bold mb-4">Find a Class</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/musical-theatre-classes-milton-keynes" className="text-white/60 hover:text-[#FF007F] transition-colors" data-testid="footer-seo-musical-theatre">Musical Theatre Classes</Link></li>
                <li><Link to="/acting-classes-milton-keynes" className="text-white/60 hover:text-[#FF007F] transition-colors" data-testid="footer-seo-acting">Acting Classes</Link></li>
                <li><Link to="/singing-classes-milton-keynes" className="text-white/60 hover:text-[#FF007F] transition-colors" data-testid="footer-seo-singing">Singing Classes</Link></li>
                <li><Link to="/dance-classes-milton-keynes" className="text-white/60 hover:text-[#FF007F] transition-colors" data-testid="footer-seo-dance">Dance Classes</Link></li>
                <li><Link to="/theatre-school-milton-keynes" className="text-white/60 hover:text-[#FF007F] transition-colors" data-testid="footer-seo-theatre-school">Theatre School Milton Keynes</Link></li>
                <li><Link to="/bootcamps" className="text-white/60 hover:text-[#FF007F] transition-colors">Holiday Bootcamps</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-lg uppercase font-bold mb-4">Locations</h4>
              <div className="space-y-3">
                <div>
                  <p className="text-white/70 text-xs font-semibold uppercase mb-1">Saturday Classes</p>
                  <p className="text-white/50 text-sm flex items-start gap-2">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Kents Hill Secondary School<br />Kents Hill Park<br />Milton Keynes<br />MK7 6BZ</span>
                  </p>
                </div>
                <div>
                  <p className="text-white/70 text-xs font-semibold uppercase mb-1">Weekday Classes</p>
                  <p className="text-white/50 text-sm flex items-start gap-2">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Watling Academy<br />Barrosa Way, Whitehouse<br />Milton Keynes<br />MK8 1EP</span>
                  </p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-lg uppercase font-bold mb-4">Contact</h4>
              <div className="space-y-3">
                <a 
                  href="tel:+447310105678" 
                  className="text-white/50 text-sm flex items-center gap-2 hover:text-[#FF007F] transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  +44 7310 105678
                </a>
                <a 
                  href="mailto:theteam@upaschool.com" 
                  className="text-white/50 text-sm flex items-center gap-2 hover:text-[#FF007F] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  theteam@upaschool.com
                </a>
                {/* Social */}
                <div className="flex items-center gap-3 pt-2">
                  <a
                    href="https://www.instagram.com/upa_school/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="UPA on Instagram"
                    data-testid="footer-social-instagram"
                    className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#FF007F] hover:bg-white/5 transition-colors"
                  >
                    <Instagram className="w-4 h-4" style={{ stroke: 'url(#upaSocialGrad)' }} />
                  </a>
                  <a
                    href="https://www.facebook.com/upaschool"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="UPA on Facebook"
                    data-testid="footer-social-facebook"
                    className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#FF007F] hover:bg-white/5 transition-colors"
                  >
                    <Facebook className="w-4 h-4" style={{ stroke: 'url(#upaSocialGrad)' }} />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-[#222] pt-8 text-center">
            <p className="text-white/30 text-sm">
              © {new Date().getFullYear()} Universal Performing Arts. All rights reserved.
            </p>
            <p className="text-white/30 text-sm mt-2">
              <Link
                to="/terms-and-conditions"
                className="hover:text-[#FF007F] transition-colors"
                data-testid="footer-terms-link"
              >
                Terms &amp; Conditions
              </Link>
              <span className="mx-2">·</span>
              <Link
                to="/privacy-policy"
                className="hover:text-[#FF007F] transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
              <span className="mx-2">·</span>
              <Link
                to="/data-deletion"
                className="hover:text-[#FF007F] transition-colors"
                data-testid="footer-data-deletion-link"
              >
                Data Deletion
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
